"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
    Flame, ArrowRight, Target, Timer,
    ShieldCheck, Database, Layers, Shield, HandHeart,
    Gift, QrCode, Star, AlertTriangle, Zap, CheckCircle2, Globe, X, Menu, Download,
} from 'lucide-react';
import { InstallPWA } from './InstallPWA';
import { Footer } from './Footer';

const Badge = ({ children, variant = "blue" }: { children: React.ReactNode, variant?: "blue" | "amber" | "red" | "green" }) => {
    const styles = {
        blue: "bg-blue-500/10 text-blue-600 border-blue-200/50",
        amber: "bg-amber-500/10 text-amber-600 border-amber-200/50",
        red: "bg-red-500/10 text-red-600 border-red-200/50",
        green: "bg-green-500/10 text-green-600 border-green-200/50",
    };
    return (
        <div className={`inline-flex items-center gap-2 border px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest backdrop-blur-md ${styles[variant]}`}>
            {children}
        </div>
    );
};

const MethodCard = ({ step, title, description, icon: Icon, colorClass }: any) => (
    <div className="group relative p-8 rounded-[2.5rem] bg-white border border-slate-100 shadow-sm hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 hover:-translate-y-2 overflow-hidden">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110 ${colorClass}`}>
            {Icon && <Icon size={28} />}
        </div>

        <div className="relative z-10 space-y-4">
            <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 text-white text-[10px] font-black">{step}</span>
                <h3 className="text-xl font-bold text-slate-900">{title}</h3>
            </div>
            <p className="text-sm text-slate-500 leading-relaxed font-medium">
                {description}
            </p>
        </div>

        {/* Background Pattern */}
        <div className="absolute -right-4 -bottom-4 w-32 h-32 bg-slate-50 rounded-full blur-3xl group-hover:bg-current opacity-10 transition-colors duration-500" />
    </div>
);

// Translation Dictionary
const translations = {
    th: {
        nav: {
            signIn: "เข้าสู่ระบบ",
            getStarted: "เริ่มใช้งานฟรี",
            installApp: "ติดตั้งแอป",
            science: "ข้อมูลทางเทคนิค",
            healthReport: "รายงานสุขภาพ"
        },
        hero: {
            badge: "วิธีเลิกบุหรี่ด้วยวิทยาศาสตร์",
            titleStart: "เลิกบุหรี่ด้วย",
            titleHighlight: "วิทยาศาสตร์ที่วัดผลได้",
            subtitle: "ระบบ Tapering อัจฉริยะที่ลดจำนวนลงทีละมวนอย่างเป็นระบบ พร้อมฟีเจอร์ช่วยจัดการความอยากและติดตามเงินออม",
            ctaPrimary: "เริ่มต้นแผนของคุณ",
            ctaSecondary: "ดูวิธีการทำงาน",
            stats: {
                success: { label: "อัตราสำเร็จ", value: "4X", sub: "สูงกว่าการหักดิบ" },
                users: { label: "ผู้ใช้งาน", value: "100+", sub: "ในช่วง Beta" },
                privacy: { label: "ความเป็นส่วนตัว", value: "100%", sub: "เก็บข้อมูลเอง" },
                cost: { label: "ค่าใช้จ่าย", value: "FREE", sub: "บริจาค/สนับสนุน" }
            }
        },
        method: {
            badge: "เทคโนโลยีหลัก",
            title: "วิธีการลดที่",
            titleHighlight: "เป็นระบบ",
            description: "เราใช้โปรโตคอลการปรับตัวทางชีวภาพ 4 ระยะ เพื่อลดอาการถอนนิโคติน",
            steps: {
                baseline: {
                    title: "สำรวจ (Baseline)",
                    desc: "วิเคราะห์ระดับการรับนิโคตินปัจจุบันของคุณเพื่อสร้าง 'ฐานความจริง' โดยไม่ต้องเปลี่ยนนิสัยในทันที"
                },
                tapering: {
                    title: "ลด (Tapering)",
                    desc: "อัลกอริทึมคำนวณเส้นโค้งการลดรายวัน ลดลง 1 มวนทุก 72 ชั่วโมงเพื่อให้สมองปรับตัวได้ทัน"
                },
                penalty: {
                    title: "วินัย (Penalty)",
                    desc: "บล็อกทางจิตวิทยา หากคุณสูบเกินโควตา ไทม์ไลน์จะถูกรีเซ็ต สร้างวินัยผ่านผลลัพธ์ทันที"
                },
                reward: {
                    title: "รางวัล (Reward)",
                    desc: "ทดแทนโดปามีนด้วยการเห็นตัวเลขเงินออมที่เพิ่มขึ้นแบบเรียลไทม์ตามเป้าหมายที่คุณตั้งไว้"
                }
            }
        },
        support: {
            badge: "ระบบช่วยเหลือนำโดย AI",
            title: "เมื่อใจอย่างเดียวไม่พอ",
            titleHighlight: "ให้วิทยาศาสตร์ช่วย",
            description: "การเดินทางไม่ได้เป็นเส้นตรง เราจึงมีเครื่องมือฉุกเฉินอย่างระบบหายใจ 4-7-8 และตัวติดตามความอยากเพื่อช่วยคุณในช่วงเวลาที่ยากลำบาก",
            features: [
                "ระบบจัดการความอยากแบบเรียลไทม์",
                "Heatmap พฤติกรรมรายชั่วโมง",
                "เป้าหมายการเงินที่กำหนดเองได้"
            ],
            ui: {
                emergency: "โหมดฉุกเฉิน",
                breathing: "เทคนิคการหายใจ 4-7-8",
                active: "ทำงานอยู่",
                nicotineFree: "ปลอดนิโคติน",
                saved: "ประหยัดไป"
            }
        },
        donation: {
            title: "สนับสนุนผู้จัดทำ",
            desc: "แอปนี้เป็น การให้ใช้งานฟรี 100% หากคุณรู้สึกว่ามันช่วยเปลี่ยนชีวิตคุณได้ คุณสามารถสนับสนุนค่ากาแฟผู้พัฒนาได้",
            button: "สนับสนุนผ่าน QR Code"
        },
        footer: {
            desc: "ระบบเลิกบุหรี่เพื่อการควบคุมนิสัย \n ออกแบบเพื่อความเป็นส่วนตัว"
        }
    },
    en: {
        nav: {
            signIn: "Sign In",
            getStarted: "Get Started",
            installApp: "Install App",
            science: "Science",
            healthReport: "Health Report"
        },
        hero: {
            badge: "Scientific Habit Control",
            titleStart: "QUIT SMARTER",
            titleHighlight: "NOT HARDER",
            subtitle: "The intelligent tapering system that works with your brain's chemistry, not against it.",
            ctaPrimary: "Start Your Journey",
            ctaSecondary: "How it works",
            stats: {
                success: { label: "Success Rate", value: "4X", sub: "Higher than cold turkey" },
                users: { label: "Users", value: "100+", sub: "Active beta testers" },
                privacy: { label: "Privacy", value: "100%", sub: "Self-hosted data" },
                cost: { label: "Cost", value: "FREE", sub: "Open Source" }
            }
        },
        method: {
            badge: "core technology",
            title: "SYSTEMATIC",
            titleHighlight: "REDUCTION",
            description: "We use a 4-phase biological adaptation protocol to minimize withdrawal symptoms.",
            steps: {
                baseline: {
                    title: "Baseline",
                    desc: "We analyze your current nicotine intake levels to establish a biological baseline without changing your habits initially."
                },
                tapering: {
                    title: "Tapering",
                    desc: "Our algorithm calculates a daily reduction curve, removing one cigarette every 72 hours to allow receptor downregulation."
                },
                penalty: {
                    title: "Penalty",
                    desc: "Psychological accountability blocks. If you exceed quota, the timeline resets, enforcing discipline through immediate feedback."
                },
                reward: {
                    title: "Reward",
                    desc: "Dopamine replacement therapy. Visualize savings accumulating in real-time as you progress towards financial goals."
                }
            }
        },
        support: {
            badge: "AI-Powered Support",
            title: "When willpower isn't enough,",
            titleHighlight: "science takes over.",
            description: "The journey isn't linear. That's why we included emergency tools like the 4-7-8 breathing assistant and craving trackers to help you manage the difficult moments.",
            features: [
                "Real-time craving intervention",
                "Hourly habit heatmaps",
                "Custom financial goals"
            ],
            ui: {
                emergency: "Emergency Mode",
                breathing: "4-7-8 Breathing Technique",
                active: "ACTIVE",
                nicotineFree: "Nicotine Free",
                saved: "Saved"
            }
        },
        donation: {
            title: "Support the Creator",
            desc: "This app is 100% Open-Source. If it helps change your life, consider buying the developer a coffee.",
            button: "Support via QR Code"
        },
        footer: {
            desc: "Smoking cessation system for habit control \n Designed for privacy"
        }
    }
};

export default function LandingPage() {
    const [lang, setLang] = useState<'th' | 'en'>('th');
    const [isScrolled, setIsScrolled] = useState(false);
    const [showQR, setShowQR] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [showInstallButton, setShowInstallButton] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const t = translations[lang];

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // PWA Install Prompt
    useEffect(() => {
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShowInstallButton(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;

        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
            setShowInstallButton(false);
        }

        setDeferredPrompt(null);
    };

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-blue-500 selection:text-white overflow-x-hidden">

            {/* 1. Navigation Bar */}
            <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 px-6 py-4 ${isScrolled ? 'bg-white/90 backdrop-blur-xl shadow-lg shadow-slate-200/50 border-b border-slate-100' : 'bg-transparent'}`}>
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3 cursor-pointer group">
                        <div className="relative">
                            <div className="absolute inset-0 bg-blue-500 blur-lg opacity-20 group-hover:opacity-40 transition-opacity rounded-full"></div>
                            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center relative rotate-3 group-hover:rotate-12 transition-transform duration-500">
                                <Flame size={20} className="text-white fill-blue-500" />
                            </div>
                        </div>
                        <span className="font-black text-xl tracking-tight">SMOKEFREE<span className="text-blue-600">PRO</span></span>
                    </div>
                    {/* Desktop Actions */}
                    <div className="hidden md:flex items-center gap-4">
                        {/* Language Toggle */}
                        <button
                            onClick={() => setLang(lang === 'th' ? 'en' : 'th')}
                            className="flex items-center gap-1 text-slate-500 hover:text-slate-900 font-bold text-xs uppercase transition-colors"
                        >
                            <Globe size={14} /> {lang.toUpperCase()}
                        </button>
                        <div className="h-4 w-px bg-slate-200 mx-2"></div>

                        {/* PWA Install Button */}
                        {showInstallButton && (
                            <>
                                <button
                                    onClick={handleInstallClick}
                                    className="hidden md:flex items-center gap-2 text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors uppercase tracking-widest"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    {t.nav.installApp}
                                </button>
                                <div className="h-4 w-px bg-slate-200 mx-2"></div>
                            </>
                        )}

                        <Link href="/science" className="hidden md:block text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors uppercase tracking-widest">
                            {t.nav.science}
                        </Link>
                        <Link href="/health-report" className="hidden md:block text-xs font-bold text-emerald-600 hover:text-emerald-700 transition-colors uppercase tracking-widest">
                            {t.nav.healthReport}
                        </Link>
                        <Link href="/login" className="hidden md:block text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors uppercase tracking-widest">{t.nav.signIn}</Link>
                        <Link href="/login" className="bg-slate-900 text-white px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-blue-600 hover:shadow-lg hover:shadow-blue-500/30 transition-all active:scale-95 flex items-center gap-2 group">
                            {t.nav.getStarted} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <button
                        onClick={() => setIsMobileMenuOpen(true)}
                        className="md:hidden text-slate-900 p-2"
                    >
                        <Menu size={24} />
                    </button>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
                <div className="fixed inset-0 z-[60] bg-white animate-in fade-in slide-in-from-right duration-300 md:hidden flex flex-col">
                    <div className="flex justify-between items-center p-6 border-b border-slate-100">
                        <span className="font-black text-xl tracking-tight">SMOKEFREE<span className="text-blue-600">PRO</span></span>
                        <button
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="bg-slate-100 p-2 rounded-full text-slate-500 hover:text-slate-900"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    <div className="flex-1 flex flex-col justify-center items-center gap-8 p-8">
                        {showInstallButton && (
                            <button
                                onClick={() => {
                                    handleInstallClick();
                                    setIsMobileMenuOpen(false);
                                }}
                                className="text-xl font-bold text-blue-600 flex items-center gap-3"
                            >
                                <Download size={24} />
                                {t.nav.installApp}
                            </button>
                        )}

                        <Link
                            href="/science"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="text-2xl font-bold text-slate-900"
                        >
                            {t.nav.science}
                        </Link>

                        <Link
                            href="/health-report"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="text-2xl font-bold text-emerald-600"
                        >
                            {t.nav.healthReport}
                        </Link>

                        <Link
                            href="/login"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="text-2xl font-bold text-slate-900"
                        >
                            {t.nav.signIn}
                        </Link>

                        <Link
                            href="/login"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="text-2xl font-black text-blue-600 flex items-center gap-2"
                        >
                            {t.nav.getStarted} <ArrowRight size={24} />
                        </Link>

                        <div className="w-full h-px bg-slate-100 my-4"></div>

                        <button
                            onClick={() => setLang(lang === 'th' ? 'en' : 'th')}
                            className="flex items-center gap-2 text-slate-500 font-bold uppercase"
                        >
                            <Globe size={20} /> {lang === 'th' ? 'Switch to English' : 'เปลี่ยนเป็นภาษาไทย'}
                        </button>
                    </div>

                    <div className="p-8 text-center bg-slate-50">
                        <p className="text-xs text-slate-400 font-bold tracking-widest uppercase">
                            Scientific Habit Control
                        </p>
                    </div>
                </div>
            )}

            {/* 2. Hero Section */}
            <header className="relative pt-44 pb-32 px-6">
                {/* Background Gradients */}
                <div className="absolute top-0 inset-x-0 h-[800px] overflow-hidden pointer-events-none">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-blue-50 to-indigo-50/0 rounded-[100%] blur-[100px] opacity-60" />
                    <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-sky-100 rounded-full blur-[120px] opacity-40 mix-blend-multiply animate-pulse" />
                    <div className="absolute top-40 left-0 w-[500px] h-[500px] bg-purple-100 rounded-full blur-[120px] opacity-40 mix-blend-multiply" />
                </div>

                <div className="max-w-6xl mx-auto text-center relative z-10">
                    <div className="inline-flex items-center gap-2 bg-white/50 backdrop-blur-md border border-slate-200/50 rounded-full pl-1 pr-4 py-1 mb-8 shadow-sm hover:scale-105 transition-transform cursor-default">
                        <div className="bg-blue-600 rounded-full p-1"><Star size={10} className="text-white fill-current" /></div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">{t.hero.badge}</span>
                    </div>

                    <h1 className="text-6xl md:text-8xl lg:text-9xl font-black mb-8 tracking-tighter leading-[0.9] text-slate-900">
                        {t.hero.titleStart} <br />
                        <span className="text-4xl md:text-6xl lg:text-7xl text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">{t.hero.titleHighlight}</span>
                    </h1>

                    <p className="text-lg md:text-2xl text-slate-500 mb-12 max-w-2xl mx-auto leading-relaxed font-medium text-balance">
                        {t.hero.subtitle}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <Link
                            href="/login"
                            className="h-14 px-8 rounded-full bg-slate-900 text-white font-bold text-lg flex items-center gap-3 hover:bg-blue-600 hover:scale-105 shadow-xl shadow-slate-200 transition-all duration-300"
                        >
                            {t.hero.ctaPrimary}
                        </Link>
                        <Link
                            href="#method"
                            className="h-14 px-8 rounded-full bg-white text-slate-900 border border-slate-200 font-bold text-lg flex items-center gap-3 hover:bg-slate-50 transition-all duration-300"
                        >
                            {t.hero.ctaSecondary}
                        </Link>
                    </div>

                    {/* Stats Teaser */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mt-20 pt-10 border-t border-slate-200/60 transform scale-110">
                        {[
                            t.hero.stats.success,
                            t.hero.stats.users,
                            t.hero.stats.privacy,
                            t.hero.stats.cost
                        ].map((stat, i) => (
                            <div key={i} className="text-center group cursor-default">
                                <div className="text-3xl font-black text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">{stat.value}</div>
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{stat.label}</div>
                                <div className="text-[10px] text-slate-400 font-medium">{stat.sub}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </header>

            {/* 3. The Methodology */}
            <section id="method" className="py-32 px-6 bg-white relative">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
                        <div className="space-y-4 max-w-2xl">
                            <Badge variant="blue"><Zap size={10} className="fill-current" /> {t.method.badge}</Badge>
                            <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-slate-900">{t.method.title} <br /><span className="text-slate-300">{t.method.titleHighlight}</span></h2>
                        </div>
                        <p className="text-slate-500 font-medium max-w-sm text-lg">
                            {t.method.description}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <MethodCard
                            step="01"
                            title={t.method.steps.baseline.title}
                            description={t.method.steps.baseline.desc}
                            icon={Target}
                            colorClass="bg-blue-100 text-blue-600"
                        />
                        <MethodCard
                            step="02"
                            title={t.method.steps.tapering.title}
                            description={t.method.steps.tapering.desc}
                            icon={Timer}
                            colorClass="bg-indigo-100 text-indigo-600"
                        />
                        <MethodCard
                            step="03"
                            title={t.method.steps.penalty.title}
                            description={t.method.steps.penalty.desc}
                            icon={AlertTriangle}
                            colorClass="bg-red-100 text-red-600"
                        />
                        <MethodCard
                            step="04"
                            title={t.method.steps.reward.title}
                            description={t.method.steps.reward.desc}
                            icon={Gift}
                            colorClass="bg-green-100 text-green-600"
                        />
                    </div>
                </div>
            </section>

            {/* 4. Support Features */}
            <section className="py-32 px-6 bg-slate-50 overflow-hidden">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                        <div className="order-2 lg:order-1 relative">
                            <div className="absolute inset-0 bg-gradient-to-tr from-blue-600 to-purple-600 rounded-[3rem] rotate-3 opacity-20 blur-2xl"></div>
                            <div className="relative bg-white border border-slate-100 rounded-[3rem] p-10 shadow-2xl space-y-8">
                                {/* Fake UI: Emergency Button */}
                                <div className="bg-red-50 rounded-3xl p-6 border border-red-100 flex items-center gap-6">
                                    <div className="w-16 h-16 bg-red-500 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-red-500/30 animate-pulse">
                                        <Flame size={32} />
                                    </div>
                                    <div>
                                        <div className="text-lg font-black text-slate-900">{t.support.ui.emergency}</div>
                                        <div className="text-sm text-slate-500">{t.support.ui.breathing}</div>
                                    </div>
                                    <div className="ml-auto bg-white px-4 py-2 rounded-full text-xs font-bold text-red-500 shadow-sm">{t.support.ui.active}</div>
                                </div>

                                {/* Fake UI: Health Stats */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                                        <div className="text-blue-500 mb-2"><Timer size={24} /></div>
                                        <div className="text-2xl font-black text-slate-900">48h</div>
                                        <div className="text-xs font-bold text-slate-400 uppercase">{t.support.ui.nicotineFree}</div>
                                    </div>
                                    <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                                        <div className="text-green-500 mb-2"><Gift size={24} /></div>
                                        <div className="text-2xl font-black text-slate-900">$120</div>
                                        <div className="text-xs font-bold text-slate-400 uppercase">{t.support.ui.saved}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="order-1 lg:order-2 space-y-8">
                            <Badge variant="amber">{t.support.badge}</Badge>
                            <h2 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 leading-tight">
                                {t.support.title} <br /><span className="text-blue-600">{t.support.titleHighlight}</span>
                            </h2>
                            <p className="text-lg text-slate-500 leading-relaxed font-medium">
                                {t.support.description}
                            </p>
                            <ul className="space-y-4 pt-4">
                                {t.support.features.map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-slate-700 font-bold">
                                        <CheckCircle2 size={20} className="text-blue-500" /> {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. Donation/Support Creator */}
            <section className="py-24 px-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-slate-100 skew-y-1 scale-110 transform origin-top-left -z-10"></div>
                <div className="max-w-4xl mx-auto text-center text-slate-900 space-y-8">
                    <div className="w-20 h-20 bg-pink-500 rounded-3xl rotate-12 flex items-center justify-center mx-auto shadow-2xl shadow-pink-500/40">
                        <HandHeart size={40} className="text-white" />
                    </div>
                    <h2 className="text-4xl md:text-5xl font-black tracking-tighter">{t.donation.title}</h2>
                    <p className="text-slate-600 text-lg md:text-xl font-medium max-w-2xl mx-auto leading-relaxed">
                        {t.donation.desc}
                    </p>
                    <button
                        onClick={() => setShowQR(true)}
                        className="bg-slate-900 text-white px-10 py-5 rounded-full font-black text-lg hover:bg-pink-500 hover:text-white transition-all shadow-xl hover:shadow-pink-500/50 flex items-center gap-3 mx-auto"
                    >
                        <QrCode size={20} /> {t.donation.button}
                    </button>
                </div>
            </section>

            {/* QR Code Modal */}
            {showQR && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity" onClick={() => setShowQR(false)} />
                    <div className="relative bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                        <button
                            onClick={() => setShowQR(false)}
                            className="absolute top-4 right-4 p-2 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-500 transition-colors z-10"
                        >
                            <X size={20} />
                        </button>

                        <div className="text-center space-y-4 flex flex-col h-full overflow-hidden">
                            <div className="space-y-1 shrink-0 mt-2">
                                <h3 className="text-xl font-bold text-slate-900">{t.donation.title}</h3>
                                <p className="text-sm text-slate-500 font-medium">Scan via Banking App</p>
                            </div>

                            <div className="bg-slate-900 p-2 rounded-2xl shadow-inner w-full">
                                <div className="relative w-full h-[60vh] bg-white rounded-xl overflow-hidden">
                                    <Image
                                        src="/donation-qr.jpg"
                                        alt="Donation QR Code"
                                        fill
                                        className="object-contain"
                                        sizes="(max-width: 768px) 100vw, 400px"
                                    />
                                </div>
                            </div>

                            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider shrink-0 pb-2">Thank you for your support</p>
                        </div>
                    </div>
                </div>
            )}


            {/* 6. Minimalist Footer */}
            <Footer theme="light" />
        </div>
    );
}
